import { ActivatableApp } from '@vendasta/marketplace-packages';
import { ResellerItem } from '@vendasta/store';
import { SortDescriptor, SortOrder } from '../sort';
import { sortedWithState } from '../utils/pipe-utils';

export function arrayDefined(arr: any[]): boolean {
  return arr && arr.length > 0;
}

// return true if the given app list contains the app is in the given activatableAppList.
// note that an AddOns parents need to be activatable in order for the AddOn to be considered activatable
export function inActivatableList(activatableAppIds: string[], appId: string, addonId?: string): boolean {
  if (addonId != null) {
    if (activatableAppIds.indexOf(appId) > -1 && activatableAppIds.indexOf(addonId) > -1) {
      return true;
    }
  } else if (activatableAppIds.indexOf(appId) > -1) {
    return true;
  }
  return false;
}

export function getApprovedResellerItems(parents: ResellerItem[], addons: ResellerItem[]): ResellerItem[] {
  if (!parents) {
    return [];
  }
  const productIds = parents.map((product) => product.appId);
  const enabledAddons = (addons || []).filter((addon) => productIds.indexOf(addon.appId) >= 0);
  return parents.concat(enabledAddons);
}

export function getActivatableAppIdsFromActivatableApps(apps: ActivatableApp[]): string[] {
  if (!apps) {
    return [];
  }
  const arr: string[] = [];
  apps.forEach((app) => {
    arr.push(app.appId);
    if (app.childActivatableApps && app.childActivatableApps.length > 0) {
      app.childActivatableApps.forEach((child) => {
        arr.push(child.appId);
      });
    }
  });
  return arr.filter((item, pos) => arr.indexOf(item) === pos);
}

export function sortResellerItems(
  items: ResellerItem[],
  fieldname: string,
  sortDescriptor: SortDescriptor,
): ResellerItem[] {
  if (sortDescriptor.fieldname !== fieldname) {
    sortDescriptor.fieldname = fieldname;
  }
  return sortByField(fieldname, sortDescriptor, items);
}

export function sortByField(fieldName: string, sortDescriptor: SortDescriptor, items: ResellerItem[]): ResellerItem[] {
  let groupsByAppId = [];
  if (items) {
    groupsByAppId = items.reduce(function (total: any, current: ResellerItem): any {
      total[current.appId] = total[current.appId] || [];
      if (!current.addonId) {
        // if it's a product, add it to the head of the array
        total[current.appId].unshift(current);
      } else {
        // if it's an addon, add it to the end of array
        total[current.appId].push(current);
      }
      const sortedAddonsWithinProduct = sortedWithState(total[current.appId].slice(1), sortDescriptor);
      Array.prototype.splice.apply(
        total[current.appId],
        [1, sortedAddonsWithinProduct.length].concat(sortedAddonsWithinProduct),
      );
      return total;
    }, Object.create(null));
  }
  let groupedArrays = [];
  for (const appIdKey in groupsByAppId) {
    if (Object.hasOwnProperty.call(groupsByAppId, appIdKey)) {
      const itemArray = groupsByAppId[appIdKey];
      groupedArrays.push(itemArray);
    }
  }
  groupedArrays = sortProductsByFieldName({
    sortDescriptor: sortDescriptor,
    fieldName: fieldName,
    arrayToBeSorted: groupedArrays,
  });
  const returnVals: ResellerItem[] = [];
  return returnVals.concat(...groupedArrays);
}

function sortProductsByFieldName(parameters: {
  sortDescriptor: SortDescriptor;
  fieldName: string;
  arrayToBeSorted: ResellerItem[];
}): ResellerItem[] {
  const { sortDescriptor, fieldName, arrayToBeSorted } = parameters;
  if (sortDescriptor.order === SortOrder.Ascending) {
    try {
      arrayToBeSorted.sort((a, b) => a[0][fieldName].localeCompare(b[0][fieldName]));
    } catch (error) {
      arrayToBeSorted.sort(function (a: any, b: any): number {
        return a[0][fieldName] > b[0][fieldName] ? 1 : b[0][fieldName] > a[0][fieldName] ? -1 : 0;
      });
    }
  } else {
    try {
      arrayToBeSorted.sort((a, b) => b[0][fieldName].localeCompare(a[0][fieldName]));
    } catch (error) {
      arrayToBeSorted.sort(function (a: any, b: any): number {
        return a[0][fieldName] < b[0][fieldName] ? 1 : b[0][fieldName] < a[0][fieldName] ? -1 : 0;
      });
    }
  }
  return arrayToBeSorted;
}

export function chunk(arr: any[], len: number): any[] {
  const chunks = [];
  let i = 0;
  const n = arr.length;

  while (i < n) {
    chunks.push(arr.slice(i, (i += len)));
  }

  return chunks;
}

interface RiKey {
  addon_id?: string;
  addonId?: string;
  appId?: string;
  app_id?: string;
  editionId?: string;
}

export function getResellerItemUniqueId(resellerItem: RiKey): string {
  if (!resellerItem) {
    return undefined;
  }
  return resellerItem.addonId || resellerItem.addon_id || resellerItem.appId || resellerItem.app_id;
}

export function resellerArrayToMap(resellerItems: ResellerItem[]): Map<string, ResellerItem> {
  if (!resellerItems || resellerItems.length === 0) {
    return new Map<string, ResellerItem>();
  }

  return resellerItems.reduce((curr, ri) => {
    curr.set(getResellerItemUniqueId(ri), ri);
    return curr;
  }, new Map<string, ResellerItem>());
}

export function mergeAppData(resellerItems: ResellerItem[], marketplaceApps: ResellerItem[]): ResellerItem[] {
  const marketplaceAppsMap: Map<string, ResellerItem[]> = marketplaceApps.reduce((map, obj) => {
    const id = getResellerItemUniqueId(obj);
    if (id) {
      map[id] = obj;
    }
    return map;
  }, new Map<string, ResellerItem[]>());

  return resellerItems.map((resellerItem) => {
    const mpApp: ResellerItem = marketplaceAppsMap[resellerItem.addonId || resellerItem.appId];
    if (mpApp) {
      const mpAppCopy = { ...mpApp };
      mpAppCopy.name = resellerItem.name;
      mpAppCopy.icon = resellerItem.icon || mpApp.icon;
      mpAppCopy.setupFee = resellerItem.setupFee;
      return mpAppCopy;
    }
    return resellerItem;
  });
}

interface RiType extends RiKey {
  isCustom?: boolean;
}
export function isAddon(resellerItem: RiType): boolean {
  return resellerItem.appId && resellerItem.addonId && !resellerItem.isCustom;
}

// Needed because usesOrderForm could be true, but no fields are actually set
export function resellerItemUsesOrderForm(resellerItem: ResellerItem): boolean {
  if (!resellerItem) {
    return false;
  }

  if (resellerItem.usesCustomizationForm) {
    return true;
  }

  if (
    !resellerItem.usesActivationSpecificEntryUrl &&
    ((!resellerItem.orderForm && !resellerItem.isCustom) || !resellerItem.usesCustomizationForm)
  ) {
    return false;
  }

  const isCustomProductThatProbablyNeedsEntryUrl = resellerItem.usesCustomizationForm && resellerItem.isCustom;
  const hasCustomOrderForm =
    resellerItem.orderForm && resellerItem.orderForm.orderForm && resellerItem.orderForm.orderForm.length > 0;
  const hasACommonFormFieldRequirement =
    resellerItem.orderForm &&
    resellerItem.orderForm.commonForm &&
    Boolean(
      Object.keys(resellerItem.orderForm.commonForm).find((key) => resellerItem.orderForm.commonForm[key] === true),
    );
  const usesActivationSpecificEntryUrl = !!resellerItem.usesActivationSpecificEntryUrl;

  return (
    isCustomProductThatProbablyNeedsEntryUrl ||
    hasCustomOrderForm ||
    hasACommonFormFieldRequirement ||
    usesActivationSpecificEntryUrl
  );
}
